// Field: label + control + helper + error (DESIGN_SYSTEM §9.1 Error, §9.2 Text input).
// The control reads ids from context, so `aria-describedby` and `aria-invalid` are always wired.
// Error text sits under the field with an icon; focus moves to the first invalid field on submit
// (see focusFirstInvalid).
import { createContext, useContext, useId, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import type { Availability } from "../state";
import { Icon } from "./Icon";

export interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
  availability: Availability | undefined;
}

const FieldContext = createContext<FieldContextValue | null>(null);
export const useFieldContext = () => useContext(FieldContext);

export interface FieldProps {
  label: ReactNode;
  /** Helper text (min/max, format hints). */
  help?: ReactNode;
  /** Validation message; sets aria-invalid on the control. */
  error?: ReactNode;
  required?: boolean;
  /** Shows "Optional" next to the label. */
  optional?: boolean;
  /** Blocked reason is shown as helper text; the control stays focusable. */
  availability?: Availability;
  /** Extra line under the control (e.g. amount in words), announced politely. */
  live?: ReactNode;
  id?: string;
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
  /** Optional test ID on the error message (additive, P4-B2). */
  errorTestId?: string;
}

export function Field({
  label,
  help,
  error,
  required = false,
  optional = false,
  availability,
  live,
  id,
  className,
  children,
  "data-testid": testId,
  errorTestId,
}: FieldProps) {
  const auto = useId();
  const fieldId = id ?? `f${auto}`;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const liveId = `${fieldId}-live`;
  const reason = availability && availability.kind !== "enabled" ? availability.reason : null;
  const describedBy =
    cx(error ? errorId : null, help || reason ? helpId : null, live ? liveId : null) || undefined;
  return (
    <FieldContext.Provider
      value={{ id: fieldId, describedBy, invalid: Boolean(error), required, availability }}
    >
      <div className={cx("flex flex-col gap-1.5", className)} data-testid={testId}>
        <label htmlFor={fieldId} className="text-sm font-medium text-fg">
          {label}
          {optional ? (
            <span className="ms-2 font-normal text-fg-tertiary">{t("common.optional")}</span>
          ) : null}
        </label>
        {children}
        {error ? (
          <p
            id={errorId}
            className="flex items-start gap-1.5 text-sm text-danger-fg"
            data-testid={errorTestId}
          >
            <Icon name="octagon" size={18} className="mt-px flex-none" />
            <span>
              <span className="ady-sr-only">{t("field.errorPrefix")} </span>
              {error}
            </span>
          </p>
        ) : null}
        {help || reason ? (
          <p id={helpId} className="text-xs text-fg-secondary">
            {reason ?? help}
          </p>
        ) : null}
        {live ? (
          <p id={liveId} className="text-xs text-fg-secondary" aria-live="polite">
            {live}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

/** Moves focus to the first control marked aria-invalid inside `root` (call after a failed submit). */
export function focusFirstInvalid(root: HTMLElement | null): void {
  const el = root?.querySelector<HTMLElement>('[aria-invalid="true"]');
  el?.focus();
}
